import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Printer, Download, FileDown, Loader2 } from 'lucide-react';
import { format } from 'date-fns';
import html2pdf from 'html2pdf.js';

const MODULE_THEMES = {
  Notice: {
    accent: '#7c3aed',
    accentLight: '#ede9fe',
    accentText: '#5b21b6',
    gradient: 'linear-gradient(135deg, #7c3aed 0%, #6d28d9 100%)',
    icon: 'N',
    title: 'Notices Schedule',
  },
  Claim: {
    accent: '#dc2626',
    accentLight: '#fee2e2',
    accentText: '#991b1b',
    gradient: 'linear-gradient(135deg, #dc2626 0%, #b91c1c 100%)',
    icon: 'C',
    title: 'Claims Schedule',
  },
  DelayEvent: {
    accent: '#d97706',
    accentLight: '#fef3c7',
    accentText: '#92400e',
    gradient: 'linear-gradient(135deg, #d97706 0%, #b45309 100%)',
    icon: 'DE',
    title: 'Delay Events Schedule',
  },
  RFI: {
    accent: '#2563eb',
    accentLight: '#dbeafe',
    accentText: '#1e40af',
    gradient: 'linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%)',
    icon: 'RFI',
    title: 'RFI Schedule',
  },
  SiteInstruction: {
    accent: '#0891b2',
    accentLight: '#cffafe',
    accentText: '#155e75',
    gradient: 'linear-gradient(135deg, #0891b2 0%, #0e7490 100%)',
    icon: 'SI',
    title: 'Site Instructions Schedule',
  },
  VariationOrder: {
    accent: '#059669',
    accentLight: '#d1fae5',
    accentText: '#065f46',
    gradient: 'linear-gradient(135deg, #059669 0%, #047857 100%)',
    icon: 'VO',
    title: 'Variation Orders Schedule',
  },
  Drawing: {
    accent: '#4f46e5',
    accentLight: '#e0e7ff',
    accentText: '#3730a3',
    gradient: 'linear-gradient(135deg, #4f46e5 0%, #4338ca 100%)',
    icon: 'DR',
    title: 'Drawings Register',
  },
  MeetingMinutes: {
    accent: '#0d9488',
    accentLight: '#ccfbf1',
    accentText: '#115e59',
    gradient: 'linear-gradient(135deg, #0d9488 0%, #0f766e 100%)',
    icon: 'MM',
    title: 'Meeting Minutes Schedule',
  },
  ShopDrawing: {
    accent: '#c026d3',
    accentLight: '#fae8ff',
    accentText: '#86198f',
    gradient: 'linear-gradient(135deg, #c026d3 0%, #a21caf 100%)',
    icon: 'SD',
    title: 'Shop Drawings Schedule',
  },
  Procurement: {
    accent: '#ea580c',
    accentLight: '#fff7ed',
    accentText: '#9a3412',
    gradient: 'linear-gradient(135deg, #ea580c 0%, #c2410c 100%)',
    icon: 'PR',
    title: 'Procurement Schedule',
  },
  IRS: {
    accent: '#0284c7',
    accentLight: '#e0f2fe',
    accentText: '#075985',
    gradient: 'linear-gradient(135deg, #0284c7 0%, #0369a1 100%)',
    icon: 'IRS',
    title: 'Information Required Schedule',
  },
  Subcontractor: {
    accent: '#65a30d',
    accentLight: '#ecfccb',
    accentText: '#3f6212',
    gradient: 'linear-gradient(135deg, #65a30d 0%, #4d7c0f 100%)',
    icon: 'SC',
    title: 'Subcontractors Schedule',
  },
};

const DEFAULT_COLUMNS = {
  Notice: [
    { key: 'notice_number', label: 'Notice #', width: '10%' },
    { key: 'event_type', label: 'Event Type', width: '22%' },
    { key: 'created_at', label: 'Created', width: '12%', formatter: 'date' },
    { key: 'submitted_at', label: 'Submitted', width: '12%', formatter: 'date' },
    { key: 'created_by', label: 'Created By', width: '14%' },
    { key: 'submitted_by', label: 'Submitted By', width: '14%' },
    { key: 'status', label: 'Status', width: '10%', formatter: 'status' },
  ],
  Claim: [
    { key: 'claim_number', label: 'Claim #', width: '10%' },
    { key: 'notice_number', label: 'Notice #', width: '10%' },
    { key: 'days_claimed', label: 'Days', width: '8%' },
    { key: 'amount_claimed', label: 'Amount', width: '12%', formatter: 'currency' },
    { key: 'created_at', label: 'Created', width: '12%', formatter: 'date' },
    { key: 'submitted_at', label: 'Submitted', width: '12%', formatter: 'date' },
    { key: 'status', label: 'Status', width: '10%', formatter: 'status' },
  ],
  DelayEvent: [
    { key: 'event_number', label: 'Event #', width: '10%' },
    { key: 'event_type', label: 'Type', width: '20%' },
    { key: 'start_date', label: 'Start', width: '11%', formatter: 'date' },
    { key: 'end_date', label: 'End', width: '11%', formatter: 'date' },
    { key: 'claimed_days', label: 'Days', width: '8%' },
    { key: 'responsible_party', label: 'Responsible', width: '14%' },
    { key: 'cease_status', label: 'Cease Status', width: '12%', formatter: 'status' },
    { key: 'status', label: 'Status', width: '10%', formatter: 'status' },
  ],
  RFI: [
    { key: 'rfi_number', label: 'RFI #', width: '8%' },
    { key: 'subject', label: 'Subject', width: '20%' },
    { key: 'discipline', label: 'Discipline', width: '10%' },
    { key: 'submitted_to', label: 'Submitted To', width: '14%' },
    { key: 'submitted_by', label: 'Submitted By', width: '12%' },
    { key: 'date_submitted', label: 'Submitted', width: '10%', formatter: 'date' },
    { key: 'response_due_date', label: 'Due', width: '10%', formatter: 'date' },
    { key: 'status', label: 'Status', width: '10%', formatter: 'status' },
  ],
  SiteInstruction: [
    { key: 'si_number', label: 'SI #', width: '10%' },
    { key: 'si_title', label: 'Title', width: '24%' },
    { key: 'discipline', label: 'Discipline', width: '12%' },
    { key: 'issued_by', label: 'Issued By', width: '14%' },
    { key: 'created_at', label: 'Created', width: '11%', formatter: 'date' },
    { key: 'response_due', label: 'Due', width: '11%', formatter: 'date' },
    { key: 'status', label: 'Status', width: '10%', formatter: 'status' },
  ],
  VariationOrder: [
    { key: 'vo_number', label: 'VO #', width: '10%' },
    { key: 'description', label: 'Description', width: '28%' },
    { key: 'total_value', label: 'Amount', width: '12%', formatter: 'currency' },
    { key: 'date_raised', label: 'Date Raised', width: '12%', formatter: 'date' },
    { key: 'priority', label: 'Priority', width: '10%' },
    { key: 'status', label: 'Status', width: '10%', formatter: 'status' },
  ],
  Drawing: [
    { key: 'drawing_number', label: 'Drawing #', width: '12%' },
    { key: 'title', label: 'Title', width: '22%' },
    { key: 'discipline', label: 'Discipline', width: '12%' },
    { key: 'revision', label: 'Rev', width: '6%' },
    { key: 'issue_purpose', label: 'Purpose', width: '12%' },
    { key: 'issued_date', label: 'Issued', width: '11%', formatter: 'date' },
    { key: 'status', label: 'Status', width: '10%', formatter: 'status' },
  ],
  MeetingMinutes: [
    { key: 'meeting_number', label: 'Meeting #', width: '10%' },
    { key: 'title', label: 'Title', width: '24%' },
    { key: 'meeting_type', label: 'Type', width: '12%' },
    { key: 'meeting_date', label: 'Date', width: '12%', formatter: 'date' },
    { key: 'attendees_count', label: 'Attendees', width: '10%', formatter: 'count' },
    { key: 'action_items_count', label: 'Actions', width: '10%', formatter: 'count' },
    { key: 'status', label: 'Status', width: '10%', formatter: 'status' },
  ],
  ShopDrawing: [
    { key: 'package_ref', label: 'Ref #', width: '10%' },
    { key: 'title', label: 'Title', width: '24%' },
    { key: 'discipline', label: 'Discipline', width: '12%' },
    { key: 'consultant', label: 'Consultant', width: '14%' },
    { key: 'submitted_date', label: 'Submitted', width: '11%', formatter: 'date' },
    { key: 'current_cycle', label: 'Rev', width: '6%' },
    { key: 'status', label: 'Status', width: '10%', formatter: 'status' },
  ],
  Procurement: [
    { key: 'item_name', label: 'Item', width: '22%' },
    { key: 'trade', label: 'Trade', width: '12%' },
    { key: 'supplier', label: 'Supplier', width: '14%' },
    { key: 'order_date', label: 'Ordered', width: '11%', formatter: 'date' },
    { key: 'delivery_date', label: 'Delivery', width: '11%', formatter: 'date' },
    { key: 'lead_time', label: 'Lead Time', width: '10%' },
    { key: 'status', label: 'Status', width: '10%', formatter: 'status' },
  ],
  IRS: [
    { key: 'reference', label: 'Ref #', width: '10%' },
    { key: 'description', label: 'Description', width: '24%' },
    { key: 'discipline', label: 'Discipline', width: '12%' },
    { key: 'required_date', label: 'Required', width: '11%', formatter: 'date' },
    { key: 'received_date', label: 'Received', width: '11%', formatter: 'date' },
    { key: 'responsible', label: 'Responsible', width: '14%' },
    { key: 'status', label: 'Status', width: '10%', formatter: 'status' },
  ],
  Subcontractor: [
    { key: 'company_name', label: 'Company', width: '20%' },
    { key: 'trade', label: 'Trade', width: '14%' },
    { key: 'subcontract_number', label: 'Contract #', width: '12%' },
    { key: 'contract_value', label: 'Value', width: '12%', formatter: 'currency' },
    { key: 'contract_start_date', label: 'Start', width: '11%', formatter: 'date' },
    { key: 'contract_end_date', label: 'End', width: '11%', formatter: 'date' },
    { key: 'email', label: 'Email', width: '16%' },
  ],
};

function formatDate(value) {
  if (!value) return '-';
  try {
    return format(new Date(value), 'dd MMM yyyy');
  } catch {
    return String(value);
  }
}

function formatCurrency(value) {
  if (!value && value !== 0) return '-';
  return `R ${Number(value).toLocaleString('en-ZA', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function formatStatus(value) {
  if (!value) return '-';
  return value.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
}

function formatCount(value) {
  if (Array.isArray(value)) return value.length;
  return value || '0';
}

function getCellValue(item, column) {
  let value = item[column.key];
  if (column.key === 'attendees_count') value = item.attendees;
  if (column.key === 'action_items_count') value = item.action_items;
  if (column.key === 'package_ref') value = item.package_ref || item.reference;
  if (column.key === 'title' && !value) value = item.package_title || item.meeting_type || item.si_title;
  if (column.key === 'item_name') value = item.item_name || item.description;

  // RFI field mappings
  if (column.key === 'subject' && !value) value = item.title;
  if (column.key === 'submitted_to' && !value) {
    const recipients = item.recipients;
    if (Array.isArray(recipients) && recipients.length > 0) {
      value = recipients.filter(r => !r.is_cc).map(r => r.name || r.email).join(', ');
    }
  }
  if (column.key === 'submitted_by' && !value) value = item.created_by_name;
  if (column.key === 'date_submitted' && !value) value = item.date_issued || item.date_created;
  if (column.key === 'response_due_date' && !value) value = item.required_response_date;

  if (column.formatter === 'date') return formatDate(value);
  if (column.formatter === 'currency') return formatCurrency(value);
  if (column.formatter === 'status') return formatStatus(value);
  if (column.formatter === 'count') return formatCount(value);
  return value || '-';
}

function getStatusColor(value) {
  if (!value) return { bg: '#f1f5f9', text: '#475569' };
  const s = value.toLowerCase().replace(/\s+/g, '_');
  const map = {
    draft: { bg: '#f1f5f9', text: '#334155', border: '#cbd5e1' },
    submitted: { bg: '#dbeafe', text: '#1e40af', border: '#93c5fd' },
    approved: { bg: '#dcfce7', text: '#166534', border: '#86efac' },
    rejected: { bg: '#fee2e2', text: '#991b1b', border: '#fca5a5' },
    open: { bg: '#dbeafe', text: '#1e40af', border: '#93c5fd' },
    closed: { bg: '#dcfce7', text: '#166534', border: '#86efac' },
    pending: { bg: '#fef3c7', text: '#92400e', border: '#fcd34d' },
    overdue: { bg: '#fee2e2', text: '#991b1b', border: '#fca5a5' },
    issued: { bg: '#dbeafe', text: '#1e40af', border: '#93c5fd' },
    awaiting_response: { bg: '#fef3c7', text: '#92400e', border: '#fcd34d' },
    answered: { bg: '#dbeafe', text: '#1e40af', border: '#93c5fd' },
    captured: { bg: '#dbeafe', text: '#1e40af', border: '#93c5fd' },
    active: { bg: '#dcfce7', text: '#166534', border: '#86efac' },
    superseded: { bg: '#fef3c7', text: '#92400e', border: '#fcd34d' },
    cancelled: { bg: '#fee2e2', text: '#991b1b', border: '#fca5a5' },
    final: { bg: '#dcfce7', text: '#166534', border: '#86efac' },
    vo_issued: { bg: '#dcfce7', text: '#166534', border: '#86efac' },
    ceased_confirmed: { bg: '#dcfce7', text: '#166534', border: '#86efac' },
    ceased_pending: { bg: '#fef3c7', text: '#92400e', border: '#fcd34d' },
    delayed: { bg: '#fee2e2', text: '#991b1b', border: '#fca5a5' },
    completed: { bg: '#dcfce7', text: '#166534', border: '#86efac' },
    in_progress: { bg: '#dbeafe', text: '#1e40af', border: '#93c5fd' },
    received: { bg: '#dcfce7', text: '#166534', border: '#86efac' },
    outstanding: { bg: '#fef3c7', text: '#92400e', border: '#fcd34d' },
    late: { bg: '#fee2e2', text: '#991b1b', border: '#fca5a5' },
    urgent: { bg: '#fee2e2', text: '#991b1b', border: '#fca5a5' },
  };
  return map[s] || { bg: '#f1f5f9', text: '#475569', border: '#cbd5e1' };
}

function computeStats(data, moduleType) {
  const total = data.length;
  if (total === 0) return [];
  switch (moduleType) {
    case 'Notice':
    case 'Claim':
      return [
        { label: 'Total', value: total },
        { label: 'Draft', value: data.filter(d => (d.status || '').toLowerCase() === 'draft').length },
        { label: 'Submitted', value: data.filter(d => (d.status || '').toLowerCase() === 'submitted').length },
      ];
    case 'DelayEvent':
      return [
        { label: 'Total', value: total },
        { label: 'Open', value: data.filter(d => d.status !== 'Closed' && d.status !== 'Resolved').length },
        { label: 'Ceased', value: data.filter(d => d.cease_status === 'ceased_confirmed').length },
      ];
    case 'RFI':
      return [
        { label: 'Total', value: total },
        { label: 'Awaiting', value: data.filter(d => d.status === 'Awaiting Response').length },
        { label: 'Answered', value: data.filter(d => d.status === 'Answered').length },
        { label: 'Overdue', value: data.filter(d => d.status === 'Overdue' || d.is_overdue).length },
      ];
    case 'SiteInstruction':
      return [
        { label: 'Total', value: total },
        { label: 'Open', value: data.filter(d => d.status !== 'Closed').length },
        { label: 'Closed', value: data.filter(d => d.status === 'Closed').length },
      ];
    case 'VariationOrder':
      return [
        { label: 'Total', value: total },
        { label: 'Issued', value: data.filter(d => d.status === 'VO Issued').length },
        { label: 'Total Value', value: formatCurrency(data.reduce((s, d) => s + (d.total_value || 0), 0)) },
      ];
    default:
      return [{ label: 'Total Items', value: total }];
  }
}

export default function PrintScheduleButton({
  moduleType,
  data = [],
  project,
  columns,
  buttonVariant = "outline",
  buttonClassName = ""
}) {
  const [previewOpen, setPreviewOpen] = useState(false);
  const [pdfLoading, setPdfLoading] = useState(false);

  const theme = MODULE_THEMES[moduleType] || MODULE_THEMES.Notice;
  const activeColumns = columns || DEFAULT_COLUMNS[moduleType] || DEFAULT_COLUMNS.Notice;
  const stats = computeStats(data, moduleType);

  const buildPrintHTML = () => {
    const statsHTML = stats.map(s =>
      `<div class="stat-item">
        <div class="stat-value">${s.value}</div>
        <div class="stat-label">${s.label}</div>
      </div>`
    ).join('');

    const headerRow = activeColumns.map(col =>
      `<th style="width:${col.width}">${col.label}</th>`
    ).join('');

    const bodyRows = data.length === 0
      ? `<tr><td colspan="${activeColumns.length}" style="text-align:center;padding:40px;color:#94a3b8;">No items to display</td></tr>`
      : data.map((item, idx) =>
          `<tr class="${idx % 2 === 0 ? 'even' : 'odd'}">
            ${activeColumns.map(col => {
              const val = getCellValue(item, col);
              if (col.formatter === 'status') {
                const sc = getStatusColor(item[col.key]);
                return `<td><span class="status-badge" style="background:${sc.bg};color:${sc.text};border:1px solid ${sc.border || sc.bg}">${val}</span></td>`;
              }
              return `<td>${val}</td>`;
            }).join('')}
          </tr>`
        ).join('');

    return `<!DOCTYPE html>
<html>
<head>
  <title>${theme.title} - ${project?.name || 'Project'}</title>
  <style>
    @import url('https://fonts.googleapis.com/css2?family=Barlow:wght@400;600;700&family=DM+Sans:wght@400;500;700&display=swap');
    * { margin: 0; padding: 0; box-sizing: border-box; }
    body { font-family: 'DM Sans', sans-serif; font-size: 10px; color: #1e293b; background: #fff; }
    
    .page { padding: 12mm 15mm; }
    
    .brand-bar {
      background: ${theme.gradient};
      padding: 18px 24px;
      color: white;
      display: flex;
      justify-content: space-between;
      align-items: center;
      border-radius: 6px 6px 0 0;
    }
    .brand-bar .logo-area {
      display: flex;
      align-items: center;
      gap: 14px;
    }
    .brand-bar .logo-icon {
      width: 40px; height: 40px;
      background: rgba(255,255,255,0.2);
      border-radius: 8px;
      display: flex;
      align-items: center;
      justify-content: center;
      font-family: 'Barlow', sans-serif;
      font-weight: 700;
      font-size: 14px;
      letter-spacing: 0.5px;
    }
    .brand-bar h1 {
      font-family: 'Barlow', sans-serif;
      font-size: 20px;
      font-weight: 700;
      text-transform: uppercase;
      letter-spacing: 1.5px;
    }
    .brand-bar .subtitle {
      font-size: 11px;
      opacity: 0.85;
      margin-top: 2px;
    }
    .brand-bar .meta {
      text-align: right;
      font-size: 10px;
      line-height: 1.6;
    }
    .brand-bar .meta strong { font-weight: 600; }

    .info-strip {
      background: #f8fafc;
      border: 1px solid #e2e8f0;
      border-top: none;
      padding: 12px 24px;
      display: flex;
      justify-content: space-between;
      align-items: center;
      font-size: 10px;
      color: #64748b;
    }
    .info-strip .project-info {
      display: flex;
      gap: 24px;
    }
    .info-strip .project-info span strong {
      color: #1e293b;
      font-weight: 600;
    }
    
    .stats-bar {
      display: flex;
      gap: 0;
      margin: 16px 0;
      border: 1px solid #e2e8f0;
      border-radius: 6px;
      overflow: hidden;
    }
    .stat-item {
      flex: 1;
      padding: 12px 16px;
      text-align: center;
      border-right: 1px solid #e2e8f0;
      background: white;
    }
    .stat-item:last-child { border-right: none; }
    .stat-value {
      font-family: 'Barlow', sans-serif;
      font-size: 20px;
      font-weight: 700;
      color: ${theme.accent};
    }
    .stat-label {
      font-size: 9px;
      text-transform: uppercase;
      letter-spacing: 0.8px;
      color: #64748b;
      font-weight: 600;
      margin-top: 2px;
    }

    table { width: 100%; border-collapse: collapse; margin-top: 4px; }
    th {
      background: #1e293b;
      color: white;
      padding: 10px 8px;
      text-align: left;
      font-family: 'Barlow', sans-serif;
      font-size: 8.5px;
      font-weight: 700;
      text-transform: uppercase;
      letter-spacing: 0.8px;
      border-bottom: 3px solid ${theme.accent};
    }
    td {
      padding: 8px;
      font-size: 9px;
      vertical-align: middle;
      border-bottom: 1px solid #e2e8f0;
      color: #334155;
    }
    tr.even { background: #ffffff; }
    tr.odd { background: #f8fafc; }

    .status-badge {
      display: inline-block;
      padding: 3px 8px;
      border-radius: 4px;
      font-size: 7.5px;
      font-weight: 700;
      text-transform: uppercase;
      letter-spacing: 0.5px;
      white-space: nowrap;
    }

    .footer {
      margin-top: 20px;
      padding-top: 12px;
      border-top: 2px solid #e2e8f0;
      display: flex;
      justify-content: space-between;
      align-items: center;
      font-size: 8.5px;
      color: #94a3b8;
    }
    .footer .branding {
      display: flex;
      align-items: center;
      gap: 6px;
    }
    .footer .branding .dot {
      width: 8px; height: 8px;
      border-radius: 2px;
      background: ${theme.accent};
    }
    .footer .branding span {
      font-family: 'Barlow', sans-serif;
      font-weight: 600;
      text-transform: uppercase;
      letter-spacing: 0.5px;
      color: #64748b;
    }
    .footer .page-info {
      color: #94a3b8;
    }

    @media print {
      body { -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important; }
      .page { padding: 8mm 10mm; }
      @page { size: landscape; margin: 8mm; }
    }
  </style>
</head>
<body>
  <div class="page">
    <div class="brand-bar">
      <div class="logo-area">
        <div class="logo-icon">${theme.icon}</div>
        <div>
          <h1>${theme.title}</h1>
          <div class="subtitle">${project?.name || 'Project'}</div>
        </div>
      </div>
      <div class="meta">
        <div><strong>Date Generated:</strong> ${format(new Date(), 'dd MMMM yyyy, HH:mm')}</div>
        <div><strong>Total Items:</strong> ${data.length}</div>
      </div>
    </div>

    <div class="info-strip">
      <div class="project-info">
        <span><strong>Project #:</strong> ${project?.project_number || project?.contract_number || '-'}</span>
        <span><strong>Contract:</strong> ${project?.contract_type || 'JBCC'}</span>
        <span><strong>Client:</strong> ${project?.client_name || '-'}</span>
      </div>
      <div>
        <span><strong>Contractor:</strong> ${project?.contractor_name || '-'}</span>
      </div>
    </div>

    ${stats.length > 0 ? `<div class="stats-bar">${statsHTML}</div>` : ''}

    <table>
      <thead><tr>${headerRow}</tr></thead>
      <tbody>${bodyRows}</tbody>
    </table>

    <div class="footer">
      <div class="branding">
        <div class="dot"></div>
        <span>Gantt Account Management Software</span>
      </div>
      <div class="page-info">Page 1 of 1 | Confidential</div>
    </div>
  </div>
</body>
</html>`;
  };

  const handlePrint = () => {
    const printWindow = window.open('', '_blank');
    printWindow.document.write(buildPrintHTML());
    printWindow.document.close();
    printWindow.focus();
    setTimeout(() => { printWindow.print(); }, 300);
  };

  const handleDownloadPDF = async () => {
    setPdfLoading(true);
    try {
      const container = document.createElement('div');
      container.innerHTML = buildPrintHTML();
      document.body.appendChild(container);
      const element = container.querySelector('.page');
      const filename = `${moduleType.toLowerCase()}_schedule_${format(new Date(), 'yyyy-MM-dd')}.pdf`;
      await html2pdf().set({
        margin: 0,
        filename,
        image: { type: 'jpeg', quality: 0.98 },
        html2canvas: { scale: 2, useCORS: true, logging: false },
        jsPDF: { unit: 'mm', format: 'a3', orientation: 'landscape' },
      }).from(element).save();
      document.body.removeChild(container);
    } catch (err) {
      console.error('PDF generation failed:', err);
    } finally {
      setPdfLoading(false);
    }
  };

  const handleExportCSV = () => {
    const headers = activeColumns.map(col => col.label).join(',');
    const rows = data.map(item =>
      activeColumns.map(col => {
        const value = getCellValue(item, col);
        const escaped = String(value).replace(/"/g, '""');
        return escaped.includes(',') ? `"${escaped}"` : escaped;
      }).join(',')
    ).join('\n');
    const csv = `${headers}\n${rows}`;
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `${moduleType.toLowerCase()}_schedule_${format(new Date(), 'yyyy-MM-dd')}.csv`;
    link.click();
    window.URL.revokeObjectURL(url);
  };

  return (
    <Dialog open={previewOpen} onOpenChange={setPreviewOpen}>
      <DialogTrigger asChild>
        <Button
          variant={buttonVariant}
          className={buttonClassName || "border-slate-900 text-slate-900 hover:bg-slate-900 hover:text-white rounded-sm font-medium uppercase tracking-wide text-xs px-3 sm:px-6 py-2 sm:py-3"}
          data-testid={`print-schedule-${moduleType.toLowerCase()}-button`}
        >
          <Printer className="h-4 w-4 sm:mr-2" />
          <span className="hidden sm:inline">Print Schedule</span>
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-6xl max-h-[90vh] overflow-hidden flex flex-col">
        <DialogHeader className="flex-shrink-0">
          <div className="flex items-center justify-between">
            <DialogTitle className="text-xl font-barlow font-bold uppercase flex items-center gap-3">
              <div
                className="w-8 h-8 rounded-md flex items-center justify-center text-white text-xs font-bold"
                style={{ background: theme.gradient }}
              >
                {theme.icon}
              </div>
              {theme.title}
            </DialogTitle>
            <div className="flex gap-2">
              <Button onClick={handleExportCSV} variant="outline" size="sm" data-testid="export-csv-button">
                <Download className="h-4 w-4 mr-2" />
                CSV
              </Button>
              <Button
                onClick={handleDownloadPDF}
                variant="outline"
                size="sm"
                disabled={pdfLoading}
                data-testid="download-pdf-button"
              >
                {pdfLoading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <FileDown className="h-4 w-4 mr-2" />}
                PDF
              </Button>
              <Button
                onClick={handlePrint}
                size="sm"
                className="text-white hover:opacity-90"
                style={{ background: theme.accent }}
                data-testid="print-button"
              >
                <Printer className="h-4 w-4 mr-2" />
                Print
              </Button>
            </div>
          </div>
        </DialogHeader>

        <div className="flex-1 overflow-auto mt-4 rounded-lg border border-slate-200">
          {/* Preview Header */}
          <div
            className="p-4 text-white flex justify-between items-center"
            style={{ background: theme.gradient }}
          >
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg flex items-center justify-center text-sm font-bold"
                style={{ background: 'rgba(255,255,255,0.2)' }}>
                {theme.icon}
              </div>
              <div>
                <h3 className="font-barlow font-bold text-lg uppercase tracking-wide">{theme.title}</h3>
                <p className="text-sm opacity-85">{project?.name || 'Project'}</p>
              </div>
            </div>
            <div className="text-right text-xs opacity-90 space-y-0.5">
              <p><strong>Project #:</strong> {project?.project_number || project?.contract_number || '-'}</p>
              <p><strong>Client:</strong> {project?.client_name || '-'}</p>
              <p><strong>Contractor:</strong> {project?.contractor_name || '-'}</p>
              <p><strong>Generated:</strong> {format(new Date(), 'dd MMM yyyy HH:mm')}</p>
              <p><strong>Items:</strong> {data.length}</p>
            </div>
          </div>

          {/* Stats Bar */}
          {stats.length > 0 && (
            <div className="grid border-b border-slate-200" style={{ gridTemplateColumns: `repeat(${stats.length}, 1fr)` }}>
              {stats.map((s, i) => (
                <div key={i} className={`p-3 text-center ${i < stats.length - 1 ? 'border-r border-slate-200' : ''}`}>
                  <div className="text-xl font-barlow font-bold" style={{ color: theme.accent }}>{s.value}</div>
                  <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">{s.label}</div>
                </div>
              ))}
            </div>
          )}

          {/* Table Preview */}
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-slate-900 text-white">
                  {activeColumns.map((col, idx) => (
                    <th
                      key={idx}
                      className="px-3 py-2.5 text-left text-[10px] font-bold uppercase tracking-wider font-barlow"
                      style={{ width: col.width, borderBottom: `3px solid ${theme.accent}` }}
                    >
                      {col.label}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {data.length === 0 ? (
                  <tr>
                    <td colSpan={activeColumns.length} className="text-center py-12 text-slate-400">
                      No items to display
                    </td>
                  </tr>
                ) : (
                  data.map((item, rowIdx) => (
                    <tr
                      key={item.id || rowIdx}
                      className={rowIdx % 2 === 0 ? 'bg-white' : 'bg-slate-50'}
                    >
                      {activeColumns.map((col, colIdx) => (
                        <td key={colIdx} className="px-3 py-2 text-xs text-slate-700 border-b border-slate-100">
                          {col.formatter === 'status' ? (
                            <span
                              className="inline-block px-2 py-0.5 rounded text-[10px] font-bold uppercase tracking-wide"
                              style={{
                                background: getStatusColor(item[col.key]).bg,
                                color: getStatusColor(item[col.key]).text,
                                border: `1px solid ${getStatusColor(item[col.key]).border || getStatusColor(item[col.key]).bg}`,
                              }}
                            >
                              {getCellValue(item, col)}
                            </span>
                          ) : (
                            getCellValue(item, col)
                          )}
                        </td>
                      ))}
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* Footer Preview */}
          <div className="p-3 border-t-2 border-slate-200 flex justify-between items-center text-[10px] text-slate-400 bg-white">
            <div className="flex items-center gap-2">
              <div className="w-2 h-2 rounded-sm" style={{ background: theme.accent }} />
              <span className="font-barlow font-semibold uppercase tracking-wider text-slate-500">Gantt Account Management Software</span>
            </div>
            <span>Page 1 of 1 | Confidential</span>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
